import React, { useState, useEffect, createContext } from "react"
import { Hub, HubCallback } from "@aws-amplify/core"
import { Auth, CognitoUser } from "@aws-amplify/auth"
import { getUserDb, IUser } from "@/features/user"

interface IAuthContext {
  currentUser: IUser | null
  isAuthenticated: boolean
  idToken: string | null
  accessToken: string | null
  refreshToken: string | null
  signIn(username: string, password: string): Promise<CognitoUser>
  signOut(): Promise<any>
  forgotPassword(username: string): Promise<any>
  forgotPasswordSubmit(username: string, code: string, password: string): Promise<any>
  answerCustomChallenge(user: any, code: string): Promise<string>
}

const signIn = (username: string, password: string): Promise<CognitoUser> => {
  return Auth.signIn(username, password)
}

const signOut = (): Promise<any> => {
  return Auth.signOut()
}

const forgotPassword = (username: string): Promise<any> => {
  return Auth.forgotPassword(username)
}

const forgotPasswordSubmit = (username: string, code: string, password: string): Promise<any> => {
  return Auth.forgotPasswordSubmit(username, code, password)
}

const answerCustomChallenge = (user: any, code: string): Promise<string> => {
  return Auth.sendCustomChallengeAnswer(user, code)
}

const AuthContext = createContext<IAuthContext>({
  currentUser: null,
  isAuthenticated: false,
  idToken: null,
  accessToken: null,
  refreshToken: null,
  signIn,
  signOut,
  forgotPassword,
  forgotPasswordSubmit,
  answerCustomChallenge,
})

const AuthProvider = (props: any) => {
  const [currentUser, setCurrentUser] = useState<IUser | null>(null)
  const [idToken, setIdToken] = useState<string | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  const authListener: HubCallback = ({ payload: { event } }) => {
    switch (event) {
      case "signIn":
        Auth.currentUserInfo().then(setCurrentUser)
        break
      case "signOut":
        setCurrentUser(null)
        setIdToken(null)
        setAccessToken(null)
        setIdToken(null)
        break
    }
  }

  useEffect(() => {
    if (currentUser?.username && !currentUser?.id) {
      getUserInfoDb()
    }
  }, [currentUser?.username])

  useEffect(() => {
    setIsAuthenticated(currentUser !== null)
  }, [currentUser])

  const getUserInfoDb = async () => {
    const json: any = await getUserDb()
    const userDbInfo: IUser = json?.data
    setCurrentUser({ ...currentUser, ...userDbInfo })
  }

  useEffect(() => {
    props.onLoaded(isLoaded)
  }, [isLoaded])

  useEffect(() => {
    if (isLoaded === false) {
      Auth.currentSession()
        .then((session) => {
          if (session && session.isValid()) {
            setIdToken(session.getIdToken().getJwtToken())
            setAccessToken(session.getAccessToken().getJwtToken())
            setRefreshToken(session.getRefreshToken().getToken())
            Auth.currentUserInfo()
              .then((user) => {
                setCurrentUser(user)
                setIsLoaded(true)
              })
              .catch(() => {
                setIsLoaded(true)
              })
          } else {
            setIsLoaded(true)
          }
        })
        .catch(() => {
          setIsLoaded(true)
        })
    }
  }, [isLoaded])

  useEffect(() => {
    Hub.listen("auth", authListener)
    return () => Hub.remove("auth", authListener)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        currentUser: currentUser,
        isAuthenticated: isAuthenticated,
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken,
        signIn: signIn,
        signOut: signOut,
        forgotPassword: forgotPassword,
        forgotPasswordSubmit: forgotPasswordSubmit,
        answerCustomChallenge: answerCustomChallenge,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
